"use client";

import { Crown, FileText, Sticker, Download } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import type { MouseEvent } from "react";
import { useLanguage } from "@/hooks/useLanguage";
import { usePWA } from "@/contexts/pwa-context";
import { translations } from "@/lib/translations";
import { FeatureGuideHint } from "@/components/guides/feature-guide-hint";
import {
	useFeatureGuide,
	useFeatureGuideCandidate,
} from "@/contexts/feature-guide-context";
import { useBabyOdinPlatformInfo } from "@/hooks/useBabyOdinPlatformInfo";
import { useBtcPrice } from "@/hooks/useBtcPrice";
import type { PlatformDisplayInfo } from "@/hooks/useBabyOdinPlatformInfo";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";

// X (Twitter) Icon Component
const XIcon = ({ className }: { className?: string }) => (
	<svg viewBox="0 0 24 24" className={className} fill="currentColor">
		<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
	</svg>
);

// Telegram Icon Component
const TelegramIcon = ({ className }: { className?: string }) => (
	<svg
		width="16"
		height="16"
		xmlns="http://www.w3.org/2000/svg"
		x="0px"
		y="0px"
		viewBox="0 0 50 50"
		className={className}
		fill="currentColor"
	>
		<path d="M 44.376953 5.9863281 C 43.889905 6.0076957 43.415817 6.1432497 42.988281 6.3144531 C 42.565113 6.4845113 40.128883 7.5243408 36.53125 9.0625 C 32.933617 10.600659 28.256963 12.603668 23.621094 14.589844 C 14.349356 18.562196 5.2382813 22.470703 5.2382812 22.470703 L 5.3046875 22.445312 C 5.3046875 22.445312 4.7547875 22.629122 4.1972656 23.017578 C 3.9185047 23.211806 3.6186028 23.462555 3.3730469 23.828125 C 3.127491 24.193695 2.9479735 24.711788 3.015625 25.259766 C 3.2532479 27.184511 5.2480469 27.730469 5.2480469 27.730469 L 5.2558594 27.734375 L 14.158203 30.78125 C 14.385177 31.538434 16.858319 39.792923 17.402344 41.541016 C 17.702797 42.507484 17.984013 43.064995 18.277344 43.445312 C 18.424133 43.635633 18.577962 43.782915 18.748047 43.890625 C 18.815627 43.933415 18.8867 43.965525 18.957031 43.994141 C 18.958531 43.994806 18.959437 43.99348 18.960938 43.994141 C 18.969579 43.997952 18.977708 43.998295 18.986328 44.001953 L 18.962891 43.996094 C 18.979231 44.002694 18.995359 44.013801 19.011719 44.019531 C 19.043456 44.030655 19.062905 44.030268 19.103516 44.039062 C 20.123059 44.395042 20.966797 43.734375 20.966797 43.734375 L 21.001953 43.707031 L 26.470703 38.634766 L 35.345703 45.554688 L 35.457031 45.605469 C 37.010484 46.295216 38.415349 45.910403 39.193359 45.277344 C 39.97137 44.644284 40.277344 43.828125 40.277344 43.828125 L 40.310547 43.742188 L 46.832031 9.7519531 C 46.998903 8.9915162 47.022612 8.334202 46.865234 7.7402344 C 46.707857 7.1462668 46.325492 6.6299361 45.845703 6.34375 C 45.365914 6.0575639 44.864001 5.9649605 44.376953 5.9863281 z M 44.429688 8.0195312 C 44.627491 8.0103707 44.774102 8.032983 44.820312 8.0605469 C 44.866523 8.0881109 44.887272 8.0844829 44.931641 8.2519531 C 44.976011 8.419423 45.000036 8.7721605 44.878906 9.3242188 L 44.875 9.3359375 L 38.390625 43.128906 C 38.375275 43.162926 38.240151 43.475531 37.931641 43.726562 C 37.616914 43.982653 37.266874 44.182554 36.337891 43.792969 L 26.632812 36.224609 L 26.359375 36.009766 L 26.353516 36.015625 L 23.451172 33.837891 L 39.761719 14.648438 A 1.0001 1.0001 0 0 0 38.974609 13 A 1.0001 1.0001 0 0 0 38.445312 13.167969 L 14.84375 28.902344 L 5.9277344 25.849609 C 5.9277344 25.849609 5.0423771 25.356927 5 25.013672 C 4.99765 24.994652 4.9871961 25.011869 5.0332031 24.943359 C 5.0792101 24.874869 5.1948546 24.759225 5.3398438 24.658203 C 5.6298218 24.456159 5.9609375 24.333984 5.9609375 24.333984 L 5.9941406 24.322266 L 6.0273438 24.308594 C 6.0273438 24.308594 15.138894 20.399882 24.410156 16.427734 C 29.045787 14.44166 33.721617 12.440122 37.318359 10.902344 C 40.914175 9.3649615 43.512419 8.2583658 43.732422 8.1699219 C 43.982886 8.0696253 44.231884 8.0286918 44.429688 8.0195312 z M 33.613281 18.792969 L 21.244141 33.345703 L 21.238281 33.351562 A 1.0001 1.0001 0 0 0 21.183594 33.423828 A 1.0001 1.0001 0 0 0 21.128906 33.507812 A 1.0001 1.0001 0 0 0 20.998047 33.892578 A 1.0001 1.0001 0 0 0 20.998047 33.900391 L 19.386719 41.146484 C 19.35993 41.068197 19.341173 41.039555 19.3125 40.947266 L 19.3125 40.945312 C 18.800713 39.30085 16.467362 31.5161 16.144531 30.439453 L 33.613281 18.792969 z M 22.640625 35.730469 L 24.863281 37.398438 L 21.597656 40.425781 L 22.640625 35.730469 z"></path>
	</svg>
);

// BTC Price Component
const BtcPriceDisplay = () => {
	const { btcPrice, isLoading } = useBtcPrice();

	if (isLoading || !btcPrice) {
		return (
			<div className="px-2 py-1 text-xs font-mono bg-white/10 rounded-md animate-pulse">
				<span className="text-white/60">BTC:</span>
				<span className="ml-1 font-semibold text-white">$---</span>
			</div>
		);
	}

	return (
		<div className="px-2 py-1 text-xs font-mono bg-white/10 rounded-md">
			<span className="text-white/60">BTC:</span>
			<span className="ml-1 font-semibold text-white">
				${Math.round(btcPrice).toLocaleString()}
			</span>
		</div>
	);
};

export default function BabyTycheBanner() {
	const { language } = useLanguage();
	const { install, isStandalone } = usePWA();
	const { isGuideActive, dismissGuide } = useFeatureGuide();
	const router = useRouter();
	const t = translations[language];
	const isInstallGuideVisible = isGuideActive("banner-install-app");
	const { platforms } = useBabyOdinPlatformInfo();
	const maxPriceUsd = Math.max(
		0,
		...platforms.map((platform) => platform.priceUsd || 0),
	);

	useFeatureGuideCandidate("banner-install-app", !isStandalone);

	const handlePlatformClick = (
		platform: PlatformDisplayInfo,
		e: MouseEvent<HTMLButtonElement>,
	) => {
		e.stopPropagation();
		window.open(platform.buyLink, "_blank", "noopener,noreferrer");
	};

	const formatPlatformPriceUsd = (priceUsd: number | null | undefined) => {
		if (priceUsd === null || priceUsd === undefined) return "$--";
		return `$${priceUsd.toFixed(4)}`;
	};

	return (
		<div
			className={`sticky top-0 w-full bg-gradient-to-r from-light-primary to-light-accent dark:from-dark-primary dark:to-dark-accent text-white shadow-md ${
				isInstallGuideVisible ? "z-layer-floating-modal" : "z-50"
			}`}
		>
			<div className="container mx-auto px-4 py-2">
				<div className="flex items-center justify-between">
					{/* 左侧：BabyOdin 基本信息 */}
					<div
						className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
						onClick={() => {
							window.location.href = "/plaza/token/2rfi";
						}}
					>
						<div className="relative w-6 h-6">
							<Image
								src="/images/baby-odin-logo.png"
								alt="BabyOdin"
								fill
								className="object-contain"
							/>
						</div>
						<span className="font-bold">BabyOdin</span>
					</div>

					{/* 中间：多平台价格展示 */}
					<TooltipProvider delayDuration={120} skipDelayDuration={80}>
						<div className="flex items-center gap-2 flex-1 justify-center mx-4 overflow-hidden">
							{platforms.map((platform) => {
								const isHighest =
									platform.priceUsd === maxPriceUsd &&
									platform.priceUsd !== null &&
									platform.priceUsd !== undefined &&
									platform.priceUsd > 0;
								const isPending =
									platform.status === "loading" || platform.isRefreshing;
								const hasError =
									platform.status === "error" &&
									!platform.priceUsd &&
									!platform.price;

								return (
									<Tooltip key={platform.id}>
										<TooltipTrigger asChild>
											<button
												type="button"
												className={`flex h-8 min-w-[96px] shrink-0 items-center justify-center gap-1.5 rounded-md px-2.5 py-1.5 text-center transition-all cursor-pointer transform hover:scale-[1.02] ${
													isHighest
														? "bg-gradient-to-r from-yellow-500/30 to-yellow-400/30 border border-yellow-400/50 shadow-lg"
														: hasError
															? "bg-red-500/10 hover:bg-red-500/15"
															: isPending
																? "bg-white/5 hover:bg-white/10"
																: "bg-white/10 hover:bg-white/20"
												}`}
												onClick={(e) => handlePlatformClick(platform, e)}
												aria-label={`${platform.name} price ${platform.satsPriceLabel}`}
											>
												<Image
													src={platform.logoSrc}
													alt=""
													width={18}
													height={18}
													className={`h-[18px] w-[18px] rounded-sm object-contain ${
														isPending ? "opacity-60" : "opacity-100"
													}`}
												/>
												<span className="sr-only">{platform.name}</span>
												<span
													className={`font-bold ${
														isHighest ? "text-yellow-100" : "text-white"
													} text-base leading-none tabular-nums ${isPending ? "animate-pulse opacity-80" : ""}`}
												>
													{formatPlatformPriceUsd(platform.priceUsd)}
												</span>
											</button>
										</TooltipTrigger>
										<TooltipContent
											side="bottom"
											sideOffset={8}
											className="z-[80] rounded-md px-2.5 py-1.5 text-xs"
										>
											<div className="font-medium leading-tight">
												{platform.name}
											</div>
											<div className="mt-1 font-mono leading-tight">
												{platform.satsPriceLabel}
											</div>
										</TooltipContent>
									</Tooltip>
								);
							})}
						</div>
					</TooltipProvider>

					{/* 右侧：BTC价格 + 社交媒体图标 */}
					<div className="flex items-center gap-2">
						{/* BTC Price Display */}
						<BtcPriceDisplay />

						{/* 分隔线 */}
						<div className="w-px h-6 bg-white/30" />

						{/* VIP Link */}
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								router.push("/profile/vip");
							}}
							className="cursor-pointer p-2 text-white/90 hover:text-white hover:bg-white/20 rounded-full transition-all duration-200 hover:scale-110 active:scale-95"
							title={t.settings.vipBenefits}
							aria-label={
								language === "zh"
									? "进入 VIP 权益页面"
									: "Open VIP benefits page"
							}
						>
							<Crown className="w-5 h-5" />
						</button>

						{/* X Account Link */}
						<div
							onClick={(e) => {
								e.stopPropagation();
								window.open("https://x.com/BabyOdinfun", "_blank");
							}}
							className="cursor-pointer p-2 text-white/90 hover:text-white hover:bg-white/20 rounded-full transition-all duration-200 hover:scale-110 active:scale-95"
							title="Follow @BabyOdinfun"
						>
							<XIcon className="w-5 h-5" />
						</div>

						{/* Telegram Link */}
						<div
							onClick={(e) => {
								e.stopPropagation();
								window.open("https://t.me/babyodinfun", "_blank");
							}}
							className="cursor-pointer p-2 text-white/90 hover:text-white hover:bg-white/20 rounded-full transition-all duration-200 hover:scale-110 active:scale-95"
							title="Join Telegram"
						>
							<TelegramIcon className="w-5 h-5" />
						</div>

						{/* Whitepaper Link */}
						<div
							onClick={(e) => {
								e.stopPropagation();
								window.open("/whitepaper", "_blank");
							}}
							className="cursor-pointer p-2 text-white/90 hover:text-white hover:bg-white/20 rounded-full transition-all duration-200 hover:scale-110 active:scale-95"
							title="Whitepaper"
						>
							<FileText className="w-5 h-5" />
						</div>

						{/* Meme Library Link */}
						<div
							onClick={(e) => {
								e.stopPropagation();
								window.open(
									"https://meme.odin.baby",
									"_blank",
									"noopener,noreferrer",
								);
							}}
							className="cursor-pointer p-2 text-white/90 hover:text-white hover:bg-white/20 rounded-full transition-all duration-200 hover:scale-110 active:scale-95"
							title={language === "zh" ? "Meme 素材库" : "Meme Library"}
						>
							<Sticker className="w-5 h-5" />
						</div>

						{/* PWA Install Link */}
						{!isStandalone && (
							<div className="relative">
								<div
									onClick={(e) => {
										e.stopPropagation();
										if (isInstallGuideVisible) {
											dismissGuide("banner-install-app");
										}
										install();
									}}
									className={`cursor-pointer p-2 text-white/90 hover:text-white hover:bg-white/20 rounded-full transition-all duration-200 hover:scale-110 active:scale-95 ${
										isInstallGuideVisible
											? "ring-2 ring-white/60 animate-pulse"
											: ""
									}`}
									title={language === "zh" ? "下载应用" : "Install App"}
								>
									<Download className="w-5 h-5" />
								</div>

								<FeatureGuideHint
									guideId="banner-install-app"
									message={(t as any).pwa.installGuideHint}
									actionLabel={(t as any).view.guideAction}
									className="absolute top-full right-0 mt-2 w-52 z-layer-floating-modal"
									bubbleClassName="bg-black/80 backdrop-blur-sm"
									arrowClassName="bg-black/80"
								/>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
